/**
 * Marketing sign-up: the inline box (footer), the popup, and the small links that go with them
 * (social icons, the WhatsApp deals channel).
 *
 * POPIA: two SEPARATE, UNTICKED boxes — email and WhatsApp — and nothing is stored without one.
 * The wording below is mirrored in the server's CONSENT_WORDING (src/shop-subscribe.ts), which
 * records the exact text and its version; change both together. No incentive is promised: the pitch
 * is the Deal of the Day and new stock, which is what the list would actually carry.
 */
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { SOCIAL_LINKS, WHATSAPP_CHANNEL_URL } from "../../../src/shop-contact";
import { IconClose, IconFacebook, IconInstagram, IconTikTok, IconWhatsApp } from "./icons";
import type { Account } from "./types";
import type { Route } from "./route";
import { Modal } from "./ui";

const EXT = { target: "_blank", rel: "noopener noreferrer" } as const;

export function SocialLinks({ className, handles }: { className?: string; handles?: boolean }) {
  const icon = { facebook: <IconFacebook size={22} />, instagram: <IconInstagram size={22} />, tiktok: <IconTikTok size={22} /> } as const;
  return (
    <ul className={`social${handles ? " social-handles" : ""}${className ? ` ${className}` : ""}`}>
      {SOCIAL_LINKS.map((s) => (
        <li key={s.id}>
          <a href={s.url} {...EXT} aria-label={`Z S MERCH on ${s.name}, ${s.handle} (opens in a new tab)`}>
            {icon[s.id]}
            {handles && <span className="social-handle" aria-hidden="true">{s.handle}</span>}
          </a>
        </li>
      ))}
    </ul>
  );
}

export function ChannelButton({ className = "btn btn-wa" }: { className?: string }) {
  return (
    <a href={WHATSAPP_CHANNEL_URL} {...EXT} className={className}>
      <IconWhatsApp size={18} /> Join our WhatsApp deals channel<span className="sr-only"> (opens in a new tab)</span>
    </a>
  );
}

type Status = { kind: "idle" } | { kind: "sending" } | { kind: "error"; message: string } | { kind: "done" };

export function SubscribeForm({ source, onDone, compact }: { source: "shop-popup" | "shop-footer"; onDone?: () => void; compact?: boolean }) {
  const uid = useId();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [emailOk, setEmailOk] = useState(false);
  const [waOk, setWaOk] = useState(false);
  const [website, setWebsite] = useState("");
  const [st, setSt] = useState<Status>({ kind: "idle" });

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!emailOk && !waOk) return setSt({ kind: "error", message: "Tick at least one box so we know how you would like to hear from us." });
    if (emailOk && !email.trim()) return setSt({ kind: "error", message: "Enter the email address you would like deals sent to." });
    if (waOk && !phone.trim()) return setSt({ kind: "error", message: "Enter the WhatsApp number you would like deals sent to." });
    setSt({ kind: "sending" });
    try {
      const r = await fetch("/api/shop/subscribe", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name, email: emailOk ? email : "", phone: waOk ? phone : "", emailConsent: emailOk, whatsappConsent: waOk, source, website }),
      });
      const b = (await r.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (!r.ok || !b.ok) return setSt({ kind: "error", message: b.error ?? "We could not sign you up just now. Please try again." });
      setSt({ kind: "done" });
      onDone?.();
    } catch {
      setSt({ kind: "error", message: "We could not sign you up just now. Please try again." });
    }
  }

  if (st.kind === "done") {
    return (
      <div className="sub-done" role="status">
        <b>Almost done.</b>
        <p>
          {emailOk ? "Check your email and press Confirm — we only email you after that. " : ""}
          {waOk ? "WhatsApp deals start once you have messaged us on that number. " : ""}
          For the quickest news, join the channel too.
        </p>
        <ChannelButton />
      </div>
    );
  }
  return (
    <form className={`sub-form${compact ? " is-compact" : ""}`} onSubmit={submit} noValidate>
      <div className="sub-fields">
        <label htmlFor={`${uid}-n`}>Name</label>
        <input id={`${uid}-n`} type="text" autoComplete="given-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
        <label htmlFor={`${uid}-e`}>Email address</label>
        <input id={`${uid}-e`} type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <label htmlFor={`${uid}-p`}>WhatsApp number</label>
        <input id={`${uid}-p`} type="tel" autoComplete="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="e.g. 079 123 4567" />
        {/* Honeypot: invisible to people and to the tab order; a script that fills it is dropped. */}
        <div className="hp" aria-hidden="true">
          <label>Leave this empty<input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} /></label>
        </div>
      </div>
      <fieldset className="sub-consent">
        <legend>How would you like to hear from us?</legend>
        <label className="check"><input type="checkbox" checked={emailOk} onChange={(e) => setEmailOk(e.target.checked)} /> <span>Email me deals and new stock</span></label>
        <label className="check"><input type="checkbox" checked={waOk} onChange={(e) => setWaOk(e.target.checked)} /> <span>WhatsApp me deals and new stock</span></label>
        <p className="sub-fine">Tick one or both, and give the matching email or number. We only use your details for what you tick, and you can ask us to stop at any time.</p>
      </fieldset>
      {st.kind === "error" && <p className="sub-err" role="alert">{st.message}</p>}
      <button type="submit" className="btn btn-action" disabled={st.kind === "sending"}>{st.kind === "sending" ? "Signing you up…" : "Get the Deal of the Day"}</button>
    </form>
  );
}

/* ── The popup ─────────────────────────────────────────────────────────────────────────────── */
const HIDE_KEY = "zs_sub_hide_until";
const VIEWS_KEY = "zs_sub_views";
const SHOWN_KEY = "zs_sub_shown";
const HIDE_DAYS = 14;
const DELAY_MS = 25_000;
/** Customer-flow pages: never interrupt a basket, a payment, an account form or an order. */
const QUIET: ReadonlyArray<Route["name"]> = ["basket", "checkout", "account", "order", "orders", "track", "verify", "reset", "welcome"];

/** localStorage/sessionStorage can throw (private mode, blocked data): every access is guarded. */
const read = (s: "local" | "session", k: string): string | null => { try { return (s === "local" ? localStorage : sessionStorage).getItem(k); } catch { return null; } };
const write = (s: "local" | "session", k: string, v: string): void => { try { (s === "local" ? localStorage : sessionStorage).setItem(k, v); } catch { /* the popup just may reappear */ } };

export function SubscribePopup({ route, account, accountReady }: { route: Route; account: Account | null; accountReady: boolean }) {
  const [open, setOpen] = useState(false);
  const routeRef = useRef(route);
  routeRef.current = route;
  const acctRef = useRef({ account, accountReady });
  acctRef.current = { account, accountReady };
  const key = `${route.name}:${"handle" in route ? route.handle : "id" in route ? route.id : ""}`;

  /** Whether the popup may appear right now. */
  const eligible = (): boolean => {
    if (QUIET.includes(routeRef.current.name)) return false;
    /** Never on top of the unanswered cookie banner — the two stacked and hid each other's buttons. */
    if (document.querySelector(".ads-consent")) return false;
    const { account: a, accountReady: ready } = acctRef.current;
    if (!ready) return false;
    if (a && a.marketingOptIn !== false) return false;
    if (read("session", SHOWN_KEY) === "1") return false;
    const until = Number(read("local", HIDE_KEY) ?? 0);
    return !(Number.isFinite(until) && until > Date.now());
  };
  const show = () => { if (eligible()) { write("session", SHOWN_KEY, "1"); setOpen(true); } };

  /** The 25-second trigger. */
  useEffect(() => {
    const t = window.setTimeout(show, DELAY_MS);
    return () => window.clearTimeout(t);
  }, []);
  /** The second page view in this visit. */
  useEffect(() => {
    const n = Number(read("session", VIEWS_KEY) ?? 0) + 1;
    write("session", VIEWS_KEY, String(n));
    if (n >= 2) { const t = window.setTimeout(show, 1500); return () => window.clearTimeout(t); }
  }, [key]);
  /** A flow page opened while it was up: get out of the way. */
  useEffect(() => { if (open && QUIET.includes(route.name)) setOpen(false); }, [route.name, open]);

  const dismiss = () => { write("local", HIDE_KEY, String(Date.now() + HIDE_DAYS * 86_400_000)); setOpen(false); };
  const finished = () => write("local", HIDE_KEY, String(Date.now() + 365 * 86_400_000));
  if (!open) return null;
  return (
    <Modal onClose={dismiss} labelledBy="sub-pop-title">
      <button type="button" className="icon-btn modal-x" onClick={dismiss} aria-label="Close sign-up"><IconClose /></button>
      <h2 id="sub-pop-title">Get the Deal of the Day and new stock first</h2>
      <SubscribeForm source="shop-popup" onDone={finished} />
      <p className="sub-or"><ChannelButton className="btn btn-quiet" /></p>
      <button type="button" className="sub-no" onClick={dismiss}>No thanks</button>
    </Modal>
  );
}
